import { FieldType, StringFieldType } from '~/src/entities/project';
import {
  type FieldOption,
  type FieldMappingOptions,
} from '~/src/entities/fieldMapping';

export const TYPE_COMPATIBILITY: Record<string, readonly string[]> = {
  SHORT_TEXT: ['SHORT_TEXT', 'MULTIPLE_CHOICE', 'DATE', 'BATCH_SELECT'],
  LONG_TEXT: [
    'SHORT_TEXT',
    'LONG_TEXT',
    'MULTIPLE_CHOICE',
    'DATE',
    'BATCH_SELECT',
  ],
  DATE: ['DATE'],
  MULTIPLE_CHOICE: ['MULTIPLE_CHOICE'],
  BATCH_SELECT: ['MULTIPLE_CHOICE', 'BATCH_SELECT'],
};

export const getAttributeTypeFromVariableType = (
  variableType: FieldType | StringFieldType,
): string => {
  if (typeof variableType === 'string') {
    return variableType;
  }

  // Convert numeric FieldType to string
  const typeMap: Record<FieldType, string> = {
    [FieldType.SHORT_TEXT]: 'SHORT_TEXT',
    [FieldType.LONG_TEXT]: 'LONG_TEXT',
    [FieldType.MULTIPLE_CHOICE]: 'MULTIPLE_CHOICE',
    [FieldType.DATE]: 'DATE',
    [FieldType.BATCH_SELECT]: 'BATCH_SELECT',
  };

  return typeMap[variableType] || 'SHORT_TEXT';
};

/**
 * Checks if a source field's attribute_type is compatible with the target field's variable_type
 * using the TYPE_COMPATIBILITY matrix.
 */
export const isFieldTypeCompatible = (
  sourceAttributeType: string,
  targetVariableType: FieldType | StringFieldType,
): boolean => {
  const targetType = getAttributeTypeFromVariableType(targetVariableType);
  const compatibleSourceTypes = TYPE_COMPATIBILITY[targetType];

  // If target type is unknown (not in matrix), be permissive and allow all - matches backend behavior
  if (!compatibleSourceTypes) {
    return true;
  }

  return compatibleSourceTypes.includes(sourceAttributeType);
};

/**
 * Filters field options by field type compatibility
 */
export const filterOptionsByFieldType = (
  options: FieldOption[],
  fieldVariableType: FieldType | StringFieldType,
): FieldOption[] => {
  return options.filter((option) =>
    isFieldTypeCompatible(option.attribute_type, fieldVariableType),
  );
};

/**
 * Sorts options alphabetically by attribute_text
 */
export const sortOptionsAlphabetically = (
  options: FieldOption[],
): FieldOption[] => {
  return [...options].sort((a, b) =>
    a.attribute_text.localeCompare(b.attribute_text),
  );
};

/**
 * Gets filtered field mapping options for a specific field.
 *
 * For role cards (contact fields): returns fields for the specific contactId
 * For matter cards: returns matter_fields
 */
export const getFilteredFieldMappingOptions = (
  allOptions: FieldMappingOptions,
  variableType: FieldType | StringFieldType,
  isRoleCard: boolean,
  contactId?: number,
): FieldOption[] => {
  let baseOptions: FieldOption[];

  if (isRoleCard) {
    if (!contactId) {
      return [];
    }
    const contactGroup = allOptions.contact_fields[String(contactId)];
    baseOptions = contactGroup?.fields ?? [];
  } else {
    baseOptions = allOptions.matter_fields;
  }

  let filtered = filterOptionsByFieldType(baseOptions, variableType);

  filtered = sortOptionsAlphabetically(filtered);

  return filtered;
};
